import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, CheckCircle, ErrorOutline, MonitorHeart, Refresh, WarningAmber } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as admin from '@/api/admin';
import type { Incident, IncidentSeverity, IncidentStatus, ServiceHealthStatus } from '@/api/admin';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { formatDateTime } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/admin/estado/')({
  component: EstadoPage,
});

const HEALTH: Record<ServiceHealthStatus, { label: string; color: 'success' | 'warning' | 'error'; icon: JSX.Element }> = {
  Operational: { label: 'Operativo', color: 'success', icon: <CheckCircle color="success" fontSize="small" /> },
  Degraded: { label: 'Degradado', color: 'warning', icon: <WarningAmber color="warning" fontSize="small" /> },
  Outage: { label: 'Caído', color: 'error', icon: <ErrorOutline color="error" fontSize="small" /> },
};

const SEVERITY: Record<IncidentSeverity, { label: string; color: 'info' | 'warning' | 'error' }> = {
  Minor: { label: 'Menor', color: 'info' },
  Major: { label: 'Mayor', color: 'warning' },
  Critical: { label: 'Crítico', color: 'error' },
};

const INC_STATUS: Record<IncidentStatus, { label: string; color: 'warning' | 'info' | 'success' | 'default' }> = {
  Investigating: { label: 'Investigando', color: 'warning' },
  Identified: { label: 'Identificado', color: 'info' },
  Monitoring: { label: 'Monitoreando', color: 'info' },
  Resolved: { label: 'Resuelto', color: 'success' },
};

type Toast = { message: string; severity: 'success' | 'error' };

function EstadoPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Toast | null>(null);
  const [opening, setOpening] = useState(false);
  const [updating, setUpdating] = useState<Incident | null>(null);
  const [openOnly, setOpenOnly] = useState(true);

  const healthQ = useQuery({ queryKey: ['service-health'], queryFn: admin.getServiceHealth, refetchInterval: 60_000 });
  const incidentsQ = useQuery({ queryKey: ['incidents', openOnly], queryFn: () => admin.listIncidents(openOnly, 0, 100) });
  const afipQ = useQuery({ queryKey: ['afip-status'], queryFn: admin.getAfipStatus, refetchInterval: 120_000 });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['incidents'] });
    void queryClient.invalidateQueries({ queryKey: ['service-health'] });
  };
  const onError = (err: unknown): void => setToast({ message: toApiError(err).message, severity: 'error' });

  if (healthQ.isLoading) return <LoadingScreen message="Cargando estado…" />;

  const report = healthQ.data;
  const services = report?.services.map((s) => s.service) ?? [];
  const incidents = incidentsQ.data?.items ?? [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonitorHeart color="primary" /> Estado & Incidentes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Salud de servicios (ARCA/WSAA/webhooks/pagos) e incidentes de plataforma.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => setOpening(true)}>Abrir incidente</Button>
      </Stack>

      {report && (
        <MuiAlert severity={report.overall === 'Operational' ? 'success' : report.overall === 'Degraded' ? 'warning' : 'error'} sx={{ mb: 2 }}>
          Estado general: <strong>{HEALTH[report.overall].label}</strong>
        </MuiAlert>
      )}

      <Grid container spacing={2} sx={{ mb: 4 }}>
        {report?.services.map((s) => (
          <Grid item xs={12} sm={6} md={4} key={s.service}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {HEALTH[s.status].icon}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.service}</Typography>
                  <Chip size="small" variant="outlined" color={HEALTH[s.status].color} label={HEALTH[s.status].label} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" gap={1}>
        <Typography variant="h6">AFIP por ambiente (WSAA / WSFE)</Typography>
        <Button size="small" startIcon={<Refresh />} disabled={afipQ.isFetching} onClick={() => afipQ.refetch()}>
          {afipQ.isFetching ? 'Probando…' : 'Reprobar'}
        </Button>
      </Stack>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {afipQ.isLoading ? (
          <Grid item xs={12}><CircularProgress size={22} /></Grid>
        ) : afipQ.isError ? (
          <Grid item xs={12}><MuiAlert severity="error">{toApiError(afipQ.error).message}</MuiAlert></Grid>
        ) : (
          (afipQ.data ?? []).map((e) => (
            <Grid item xs={12} sm={6} md={3} key={`${e.service}-${e.environment}`}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  {e.reachable ? <CheckCircle color="success" fontSize="small" /> : <ErrorOutline color="error" fontSize="small" />}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {e.service} · {e.environment === 'Production' ? 'Prod' : 'Homolog'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {e.reachable ? `arriba · ${e.latencyMs} ms` : e.error ?? 'caído'}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">Incidentes</Typography>
        <FormControlLabel control={<Checkbox size="small" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />} label="Solo abiertos" />
      </Stack>

      {incidentsQ.isLoading ? (
        <CircularProgress size={24} />
      ) : incidents.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" align="center">Sin incidentes {openOnly ? 'abiertos' : 'registrados'}.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1.5}>
          {incidents.map((inc) => (
            <Paper key={inc.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{inc.title}</Typography>
                    <Chip size="small" variant="outlined" color={SEVERITY[inc.severity].color} label={SEVERITY[inc.severity].label} />
                    <Chip size="small" color={INC_STATUS[inc.status].color} label={INC_STATUS[inc.status].label} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {inc.affectedServices.join(', ') || 'sin servicios'} · inicio {formatDateTime(inc.startedAt)}
                    {inc.resolvedAt ? ` · resuelto ${formatDateTime(inc.resolvedAt)}` : ''}
                  </Typography>
                  {inc.updates.length > 0 && (
                    <Box sx={{ mt: 1, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                      {[...inc.updates].reverse().slice(0, 3).map((u, i) => (
                        <Typography key={i} variant="caption" sx={{ display: 'block' }} color="text.secondary">
                          {formatDateTime(u.at)} · <strong>{INC_STATUS[u.status].label}</strong> — {u.message}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
                {inc.status !== 'Resolved' && (
                  <Button size="small" onClick={() => setUpdating(inc)}>Agregar update</Button>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {opening && (
        <OpenIncidentDialog
          services={services}
          onClose={() => setOpening(false)}
          onDone={() => { setOpening(false); invalidate(); setToast({ message: 'Incidente abierto.', severity: 'success' }); }}
          onError={onError}
        />
      )}
      {updating && (
        <UpdateIncidentDialog
          incident={updating}
          onClose={() => setUpdating(null)}
          onDone={() => { setUpdating(null); invalidate(); setToast({ message: 'Update publicado.', severity: 'success' }); }}
          onError={onError}
        />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">{toast?.message}</MuiAlert>
      </Snackbar>
    </Box>
  );
}

function OpenIncidentDialog({
  services,
  onClose,
  onDone,
  onError,
}: {
  services: string[];
  onClose: () => void;
  onDone: () => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('Minor');
  const [affected, setAffected] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const mut = useMutation({
    mutationFn: () => admin.openIncident({ title: title.trim(), severity, affectedServices: affected, message: message.trim() }),
    onSuccess: onDone,
    onError,
  });

  const toggle = (s: string): void => setAffected((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  const valid = title.trim() !== '' && message.trim() !== '';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Abrir incidente</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField label="Título" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          <TextField select label="Severidad" fullWidth value={severity} onChange={(e) => setSeverity(e.target.value as IncidentSeverity)}>
            {(Object.keys(SEVERITY) as IncidentSeverity[]).map((s) => <MenuItem key={s} value={s}>{SEVERITY[s].label}</MenuItem>)}
          </TextField>
          {services.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Servicios afectados</Typography>
              {services.map((s) => (
                <FormControlLabel key={s} control={<Checkbox size="small" checked={affected.includes(s)} onChange={() => toggle(s)} />} label={<Typography variant="body2">{s}</Typography>} />
              ))}
            </Box>
          )}
          <TextField label="Mensaje inicial" fullWidth multiline minRows={2} value={message} onChange={(e) => setMessage(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={!valid || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : 'Abrir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UpdateIncidentDialog({
  incident,
  onClose,
  onDone,
  onError,
}: {
  incident: Incident;
  onClose: () => void;
  onDone: () => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const [status, setStatus] = useState<IncidentStatus>(incident.status);
  const [message, setMessage] = useState('');

  const mut = useMutation({
    mutationFn: () => admin.postIncidentUpdate(incident.id, { status, message: message.trim() }),
    onSuccess: onDone,
    onError,
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar update · {incident.title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField select label="Estado" fullWidth value={status} onChange={(e) => setStatus(e.target.value as IncidentStatus)}>
            {(Object.keys(INC_STATUS) as IncidentStatus[]).map((s) => <MenuItem key={s} value={s}>{INC_STATUS[s].label}</MenuItem>)}
          </TextField>
          <TextField label="Mensaje" fullWidth multiline minRows={2} value={message} onChange={(e) => setMessage(e.target.value)} autoFocus />
          <Divider />
          <Typography variant="caption" color="text.secondary">Marcar “Resuelto” cierra el incidente y vuelve los servicios afectados a operativo.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={message.trim() === '' || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : 'Publicar update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
